import classNames from 'classnames';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@ctct/payflow-ui';

import paypalBuyNowIcon from './assets/paypal_logo.webp';
import { PayPalLogo } from './PayPalLogo';
import type { PaymentMethodsLoadedProps } from '../widgets/PaymentMethodsWidget/types';

interface Props {
  data: Pick<PaymentMethodsLoadedProps['data'], 'getAmazonButtonId' | 'isPaypalLoading'>;
  handlers: Pick<
    PaymentMethodsLoadedProps['handlers'],
    'renderAmazonButton' | 'handleAmazonToken' | 'initPaypalTokenFromUrl' | 'onNavigateToPaypalClick'
  >;
  hooks: Pick<PaymentMethodsLoadedProps['hooks'], 'useAmazonPayConnected' | 'usePaypalConnected' | 'useTabsLoading'>;
  showBuyNowButton?: boolean;
}

export const PayPal = ({ data, handlers, hooks, showBuyNowButton }: Props) => {
  const { t } = useTranslation();
  const paypalConnected = hooks.usePaypalConnected();

  const tabsLoading = hooks.useTabsLoading();

  useEffect(() => {
    handlers.initPaypalTokenFromUrl();
  }, [handlers]);

  if (paypalConnected) {
    return (
      <div className={classNames('paypal-connected-container', showBuyNowButton && 'buynow-paypal-button')}>
        <p>{t('payment-methods.paypal_success')}</p>
        <p>{t('payment-methods.paypal_success_description_1')}</p>
        <p>{t('payment-methods.paypal_success_description_2')}</p>
        <Button
          data-qe-id="update-paypal-token"
          variant="secondary"
          className="paypal-button paypal-connected"
          disabled={data.isPaypalLoading() || tabsLoading}
          busy={data.isPaypalLoading() || tabsLoading}
          onClick={handlers.onNavigateToPaypalClick}
        >
          {t('payment-methods.paypal_exist')} <PayPalLogo />
        </Button>
      </div>
    );
  }

  return showBuyNowButton ? (
    <div className="paypal-body-content-block buynow-paypal-button" data-qe-id="paypal-body">
      <button
        type="button"
        disabled={data.isPaypalLoading() || tabsLoading}
        className="paypal-button"
        data-qe-id="continue-to-paypal"
        onClick={handlers.onNavigateToPaypalClick}
      >
        <div className="paypal-button-content">
          <div className="paypal-text">
            {data.isPaypalLoading() ||
              (tabsLoading && (
                <div className="button-image">
                  <i className="fa fa-refresh fa-spin" aria-hidden="true" />
                </div>
              ))}
            {t('payment-methods.paypal_setup_short')} <img src={paypalBuyNowIcon} alt="PayPal" />
          </div>
        </div>
      </button>
    </div>
  ) : (
    <Button
      className="paypal-button"
      data-qe-id="continue-to-paypal"
      variant="primary"
      disabled={data.isPaypalLoading() || tabsLoading}
      busy={data.isPaypalLoading() || tabsLoading}
      onClick={handlers.onNavigateToPaypalClick}
    >
      {t('payment-methods.paypal_setup_short')}
      <PayPalLogo />
    </Button>
  );
};
